"use client";

import { cn } from "@/lib/tailwind";
import { ElementRef, MouseEvent, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Menu } from "./Menu";
import { Trigger } from "./Trigger";
import { ISelectProps } from "./types";

const Select = ({
  open,
  value,
  error,
  onOpen,
  onClose,
  options,
  disabled,
  onChange,
  menuProps,
  parentProps,
  triggerLabel,
  triggerProps,
  slots = {},
  searchable,
  overlap = true,
  noResultFoundLabel = "No Result Found!",
}: ISelectProps) => {
  const [openMenu, setOpenMenu] = useState(open ?? false);
  const [menuPosition, setMenuPosition] = useState<"bottom" | "top">("bottom");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSpacePress, setIsSpacePress] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // No selection-order tracking for Select (selected-first removed)

  const { error: errorContent, variant = "contained" } = error ?? {};
  const selectedValue = options.find((option) => option.value === value);

  // Refs for DOM elements
  const menuRef = useRef<ElementRef<"ul">>(null);
  const triggerRef = useRef<ElementRef<"button">>(null);
  const selectRef = useRef<ElementRef<"fieldset">>(null);

  // Generate unique IDs for accessibility
  const selectId = useId();
  const errorId = `${selectId}-error`;

  const handleSelectItem = (
    event: MouseEvent<HTMLLIElement, globalThis.MouseEvent>,
    value: string | number,
  ) => {
    event.preventDefault();
    onChange(event, value);
    setOpenMenu(false);
    onClose?.();
    setIsSpacePress(false);
    // no selected-first ordering
  };

  const handleToggleOpen = () => {
    if (!isSpacePress) {
      if (!openMenu) {
        setOpenMenu(true);
        onOpen?.();
        return;
      }
      setOpenMenu(false);
      onClose?.();
      setIsSpacePress(false);
    }
  };

  const handleClickOutside = (event: globalThis.MouseEvent) => {
    if (
      selectRef.current &&
      !selectRef.current.contains(event.target as Node) &&
      menuRef.current &&
      !menuRef.current.contains(event.target as Node)
    ) {
      setOpenMenu(false);
      onClose?.();
    }
  };

  const handleMenuPosition = () => {
    if (!menuRef.current || !selectRef.current) return;
    const rect = selectRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - rect.bottom;
    setMenuPosition(spaceBelow < menuRef.current.offsetHeight ? "top" : "bottom");
  };

  useEffect(() => {
    setOpenMenu(open ?? openMenu);
  }, [open]);

  useLayoutEffect(() => {
    if (openMenu) {
      handleMenuPosition();
      document.addEventListener("click", handleClickOutside);
    } else document.removeEventListener("click", handleClickOutside);

    return () => document.removeEventListener("click", handleClickOutside);
  }, [openMenu]);

  // Auto focus trigger search when opening
  useEffect(() => {
    if (openMenu && searchable) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [openMenu, searchable]);

  // Initialize search input with current selected label when opening
  useEffect(() => {
    if (openMenu && searchable) {
      setSearchQuery("");
    }
  }, [openMenu, searchable, selectedValue]);

  // compute display options with search only (no selected-first ordering)
  const displayOptions = (() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? options.filter((o) =>
          (typeof o.label === "string" ? o.label : String(o.label)).toLowerCase().includes(q),
        )
      : options.slice();

    return filtered;
  })();

  const computedLabel =
    openMenu && searchable ? (
      <input
        ref={searchInputRef}
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        placeholder={typeof triggerLabel === "string" ? triggerLabel : "Search..."}
        className="w-full border-none bg-transparent p-0 text-[0.813rem] text-black placeholder:text-gray-500 focus:outline-none focus:ring-0"
      />
    ) : selectedValue ? (
      selectedValue.label
    ) : (
      triggerLabel
    );

  return (
    <fieldset
      ref={selectRef}
      disabled={disabled}
      {...parentProps}
      id={selectId + ""}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? errorId : undefined}
      className={cn(
        "relative cursor-pointer",
        disabled && "cursor-not-allowed opacity-50",
        parentProps?.className,
      )}
    >
      <Trigger
        error={error}
        ref={triggerRef}
        disabled={disabled}
        onKeyDown={(e) => {
          if (e.code === "Space") {
            setIsSpacePress(true);
          } else {
            setIsSpacePress(false);
          }
        }}
        {...triggerProps}
        onToggle={handleToggleOpen}
        label={computedLabel}
        endAction={slots?.endAction?.action}
      />
      {options.length > 0 && (
        <Menu
          value={value}
          open={openMenu}
          menuRef={menuRef}
          options={displayOptions}
          menuProps={menuProps}
          menuPosition={menuPosition}
          onSelect={handleSelectItem}
          triggerWidth={triggerRef?.current?.offsetWidth}
          searchable={false}
          searchQuery={undefined}
          onSearchChange={undefined}
          overlap={overlap}
          noResultFoundLabel={noResultFoundLabel}
        />
      )}
      {errorContent && (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          {...error}
          className={cn(
            "w-full text-sm text-secondary-1",
            openMenu && "absolute",
            variant === "transparent" && "mt-1",
            variant === "contained" && "bg-error p-4",
            error?.className,
          )}
        >
          {errorContent}
        </p>
      )}
    </fieldset>
  );
};

export default Select;
